(function () {
    'use strict';

    angular
        .module('app')
        .factory('FieldSelectorService', FieldSelectorService);

    FieldSelectorService.$inject = ['$q', 'DialogService', 'EntityTemplatesService'];

    /**
     * The field selector allows a user to select fields from an entity template.
     */
    function FieldSelectorService($q, DialogService, EntityTemplatesService) {
        var service = {
            displayDialogForEntityTemplate: displayDialogForEntityTemplate,
            fieldFormatters: {
                mergeFields: FieldFormatterTemplateMerge(),
                entityPreview: FieldFormatterEntityPreview(),
                simple: FieldFormatterSimple()
            },
            convertEntityTemplateFields: convertEntityTemplateFields,
            getDocumentTemplateFields: getDocumentTemplateFields,
            getFileFields: getFileFields,
            getEntitySystemFields: getEntitySystemFields,
            getEntitySystemForPreviewFields: getEntitySystemForPreviewFields,
            getEntitySystemForSearchFields: getEntitySystemForSearchFields
        };

        return service;

        /**
         * Display a dialog allowing the user to select a field from the entity template.
         *
         * @param {object} entityTemplate
         * @param {object} options - Optional. An object with the following optional properties:
         *   {bool} multiSelect
         *   {object} fieldFormatter - One of the field formatters in FieldSelectorService.fieldFormatters
         *   {array} selectedFields - Array of {string} for selected fields, in the format of the
         *     provided field formatter, e.g. ['[[ChilliDocs:Cats.Species]]', '[[ChilliDocs:Cats.Name]]']
         *   {bool} firstLevelFieldsOnly - True if only the first-level fields of the entity template should be shown.
         *     also excludes linked and embedded entities.
         *   {bool} includeOrganisation - True if the organisation entity template should be included.
         *   {bool} includeCurrentUser - True if the current user should be included.
         *   {bool} includeDocumentTemplate - True if the document template fields should be included.
         *   {bool} includeFileDetails - True if the 'File' fields should be included.
         *   {bool} excludeFileDetailsName - True if the 'Name' field for 'File' should not be included.
         *   {bool} includeEntitySystem - True if the entity system fields should be included.
         *   {bool} includeEntitySystemForPreview - True if the entity system fields for a preview should be included.
         *   {bool} includeEntitySystemForSearch - True if the entity system fields for a search should be included.
         *
         * @return {promise}
         *   # Reject with no returned object means dialog was cancelled.
         *   # Reject with a returned object indicating an error.
         *   # Resolves with {array} A list of {string} representing field expressions for the selected fields,
         *     e.g. ['[[ChilliDocs:Cats.Species]]', '[[ChilliDocs:Cats.Name]]']
         */
        function displayDialogForEntityTemplate(entityTemplate, options) {

            //
            // Defaults, and overrides based on options object.
            //

            var multiSelect = false;
            var fieldFormatter = service.fieldFormatters.mergeFields;
            var selectedFields = null;
            var firstLevelFieldsOnly = false;
            var includeOrganisation = false;
            var includeCurrentUser = false;
            var includeDocumentTemplate = false;
            var includeFileDetails = false;
            var excludeFileDetailsName = false;
            var includeEntitySystem = false;
            var includeEntitySystemForPreview = false;
            var includeEntitySystemForSearch = false;

            if (!_.isNil(options)) {
                if (!_.isNil(options.multiSelect)) {
                    multiSelect = options.multiSelect;
                }

                if (!_.isNil(options.fieldFormatter)) {
                    fieldFormatter = options.fieldFormatter;
                }

                if (!_.isNil(options.selectedFields)) {
                    // Take a copy to avoid 2-way binding
                    selectedFields = options.selectedFields.slice();
                }

                if (!_.isNil(options.firstLevelFieldsOnly)) {
                    firstLevelFieldsOnly = options.firstLevelFieldsOnly;
                }

                if (!_.isNil(options.includeOrganisation)) {
                    includeOrganisation = options.includeOrganisation;
                }

                if (!_.isNil(options.includeCurrentUser)) {
                    includeCurrentUser = options.includeCurrentUser;
                }

                if (!_.isNil(options.includeDocumentTemplate)) {
                    includeDocumentTemplate = options.includeDocumentTemplate;
                }

                if (!_.isNil(options.includeFileDetails)) {
                    includeFileDetails = options.includeFileDetails;
                }

                if (!_.isNil(options.excludeFileDetailsName)) {
                    excludeFileDetailsName = options.excludeFileDetailsName;
                }

                if (!_.isNil(options.includeEntitySystem)) {
                    includeEntitySystem = options.includeEntitySystem;
                }

                if (!_.isNil(options.includeEntitySystemForPreview)) {
                    includeEntitySystemForPreview = options.includeEntitySystemForPreview;
                }

                if (!_.isNil(options.includeEntitySystemForSearch)) {
                    includeEntitySystemForSearch = options.includeEntitySystemForSearch;
                }
            }

            //
            // Input validation
            //

            if (_.isNil(entityTemplate.Fields)) {
                throw 'Cannot display field selector, the provided entity template has no Fields property.';
            }


            //
            // Fetch necessary data
            //

            var getRelatedEntityTemplatesQry = EntityTemplatesService.getRelatedEntityTemplates(entityTemplate);
            var getOrganisationTemplateQry;
            var getOrganisationRelatedTemplatesQry;
            var getCurrentUserTemplateQry;
            var getCurrentUserRelatedTemplatesQry;
            var getGeneralTemplateQry;

            if (includeOrganisation) {
                getOrganisationTemplateQry = EntityTemplatesService.query({ entityType: 'Organisation' });
                getOrganisationRelatedTemplatesQry = getOrganisationTemplateQry.then(function (organisationTemplate) {
                    return EntityTemplatesService.getRelatedEntityTemplates(organisationTemplate.EntityTemplates[0]);
                });
            } else {
                getOrganisationTemplateQry = $q.when({ EntityTemplates: []});
                getOrganisationRelatedTemplatesQry = $q.when(null);
            }

            if (includeCurrentUser) {
                getCurrentUserTemplateQry = EntityTemplatesService.query({ entityType: 'User' });
                getCurrentUserRelatedTemplatesQry = getCurrentUserTemplateQry.then(function (userTemplate) {
                    return EntityTemplatesService.getRelatedEntityTemplates(userTemplate.EntityTemplates[0]);
                });
            } else {
                getCurrentUserTemplateQry = $q.when({ EntityTemplates: [] });
                getCurrentUserRelatedTemplatesQry = $q.when(null);
            }

            //
            // Collate data and present dialog
            //

            var relatedEntityTemplates;

            return $q.all([
                getRelatedEntityTemplatesQry,
                getOrganisationTemplateQry,
                getOrganisationRelatedTemplatesQry,
                getCurrentUserTemplateQry,
                getCurrentUserRelatedTemplatesQry
            ]).then(function (result) {
                relatedEntityTemplates = _.union(result[0], result[1].EntityTemplates, result[2], result[3], result[4], result[5]);
                return presentDialog();
            });

            function presentDialog() {
                return DialogService.show({
                    controller: 'FieldSelectorDialog',
                    controllerAs: 'vm',
                    templateUrl: 'app/FieldSelector/field-selector-dialog.tmpl.html',
                    locals: {
                        entityTemplate: entityTemplate,
                        entityTemplates: relatedEntityTemplates,
                        includeOrganisation: includeOrganisation,
                        includeCurrentUser: includeCurrentUser,
                        includeDocumentTemplate: includeDocumentTemplate,
                        includeFileDetails: includeFileDetails,
                        excludeFileDetailsName: excludeFileDetailsName,
                        includeEntitySystem: includeEntitySystem,
                        includeEntitySystemForPreview: includeEntitySystemForPreview,
                        includeEntitySystemForSearch: includeEntitySystemForSearch,
                        firstLevelFieldsOnly: firstLevelFieldsOnly,
                        multiSelect: multiSelect,
                        fieldFormatter: fieldFormatter,
                        selectedFields: selectedFields
                    }
                }).then(function (selectedFields) {
                    return selectedFields;
                });
            }
        }

        /**
         * Tree control needs its own data structure to support hierarchy.
         * Convert entity template fields into tree hierarchy.
         *
         * @param {object} options - An options object with the following properties:
         *   {array} fields - An array of entity template fields.
         *   {array} templates - An array of related entity templates.
         *   {object} fieldFormatter - Optional. One of the field formatters in FieldSelectorService.fieldFormatters
         *   {bool} firstLevelFieldsOnly - Optional. True if only the first-level fields of the entity template should be shown.
         *   {string} fieldExpressionPrefix - Optional.
         *   {array} parentTemplates - Optional. Working variable, for internal use only.
         *     Used internally to keep track of parent templates to avoid infinite recursion for self-referencing templates.
         *     An array of {number} entity id.
         *   {array} parentNodes - Optional. Working variable, for internal use only. An array of the parent nodes to help calculating expressions.
         *
         * @return {array} An array of nodes for use in the field selector tree control.
         */
        function convertEntityTemplateFields(options) {

            //
            // Input parameters, and defaults
            //

            var fields = options.fields;
            var templates = options.templates;
            var fieldFormatter = service.fieldFormatters.mergeFields;
            var firstLevelFieldsOnly = false;
            var fieldExpressionPrefix = null;
            var parentTemplates = [];
            var parentNodes = [];
            
            if (!_.isNil(options.fieldFormatter)) {
                fieldFormatter = options.fieldFormatter;
            }

            if (!_.isNil(options.firstLevelFieldsOnly)) {
                firstLevelFieldsOnly = options.firstLevelFieldsOnly;
            }

            if (!_.isNil(options.fieldExpressionPrefix)) {
                fieldExpressionPrefix = options.fieldExpressionPrefix;
            }

            if (!_.isNil(options.parentTemplates)) {
                parentTemplates = options.parentTemplates;
            }

            if (!_.isNil(options.parentNodes)) {
                parentNodes = options.parentNodes;
            }


            //////////////////////////////

            var result = [];

            _.forEach(fields, function (field) {

                // If this is a relationship field (embedded or linked entity), it's only include if 'firstLevelFieldsOnly' is false.

                if (field.FieldType !== 'Relationship' || !firstLevelFieldsOnly) {
                    var node = {
                        label: field.Name,
                        parentTemplates: _.clone(parentTemplates),
                        field: field
                    };

                    var nodePath = parentNodes.slice();
                    nodePath.push(node);
                    node.fieldExpression = fieldFormatter.convertNodePathToExpression(nodePath, fieldExpressionPrefix);

                    if (field.FieldType === 'Relationship') {
                        var relatedEntityTemplate = _.find(templates, 'Id', field.EntityTemplateId);

                        if (_.isNil(relatedEntityTemplate)) {
                            throw 'Could not find related entity template: ' + field.EntityTemplateId;
                        }

                        node.relationshipFieldName = relatedEntityTemplate.Name;

                        // Prevent infinite recursion by preventing a related entity template being repeatedly added
                        if (!_.includes(parentTemplates, field.EntityTemplateId)) {
                            var childsParents = _.clone(parentTemplates);
                            childsParents.push(field.EntityTemplateId);

                            node.children = convertEntityTemplateFields({
                                fields: relatedEntityTemplate.Fields,
                                templates: templates,
                                fieldFormatter: fieldFormatter,
                                firstLevelFieldsOnly: firstLevelFieldsOnly,
                                fieldExpressionPrefix: fieldExpressionPrefix,
                                parentTemplates: childsParents,
                                parentNodes: nodePath
                            });

                            result.push(node);
                        }
                    } else {
                        result.push(node);
                    }
                }
            });

            return result;
        }

        /**
         * Return the 'Document Template' system fields in a structure supposed by the Tree control.
         *
         * @param {object} fieldFormatter - Optional. One of the field formatters in FieldSelectorService.fieldFormatters
         *
         * @return {array} An array of nodes for use in the field selector tree control.
        */
        function getDocumentTemplateFields(fieldFormatter) {
            return convertEntityTemplateFields({
                fields: [
                    {
                        Default: null,
                        Description: '',
                        FieldType: 'Text',
                        HelpText: null,
                        Length: null,
                        Mandatory: true,
                        Name: 'Name',
                        Visible: true,
                        Width: 0
                    },
                    {
                        Default: null,
                        Description: '',
                        FieldType: 'Text',
                        HelpText: null,
                        Length: null,
                        Mandatory: false,
                        Name: 'Description',
                        Visible: true,
                        Width: 0
                    },
                    {
                        Default: null,
                        Description: '',
                        FieldType: 'Text',
                        HelpText: null,
                        Length: null,
                        Mandatory: true,
                        Name: 'Entity Template Name',
                        Visible: true,
                        Width: 0
                    }
                ],
                fieldFormatter: fieldFormatter,
                fieldExpressionPrefix: '$.Document Template.'
            });
        }

        /**
         * Return the 'File' system fields in a structure supposed by the Tree control.
         *
         * @param {object} fieldFormatter - Optional. One of the field formatters in FieldSelectorService.fieldFormatters
         * @param {bool} excludeFilename - Optional. If true, the file name field will be excluded
         *
         * @return {array} An array of nodes for use in the field selector tree control.
        */
        function getFileFields(fieldFormatter, excludeFilename) {
            var fields = [
                    {
                        Default: null,
                        Description: '',
                        FieldType: 'Number',
                        HelpText: null,
                        Length: null,
                        Mandatory: true,
                        Name: 'ID',
                        Visible: true,
                        Width: 0
                    }];
            
            if (_.isNil(excludeFilename) || !excludeFilename) {
                fields.push({
                    Default: null,
                    Description: '',
                    FieldType: 'Text',
                    HelpText: null,
                    Length: null,
                    Mandatory: true,
                    Name: 'Name',
                    Visible: true,
                    Width: 0
                });
            }

            fields.push({
                Default: null,
                Description: '',
                FieldType: 'Date',
                HelpText: null,
                Length: null,
                Mandatory: true,
                Name: 'Date',
                Visible: true,
                Width: 0
            });

            fields.push({
                Default: null,
                Description: '',
                FieldType: 'Text',
                HelpText: null,
                Length: null,
                Mandatory: true,
                Name: 'Time',
                Visible: true,
                Width: 0
            });

            return convertEntityTemplateFields({
                fields: fields,
                fieldFormatter: fieldFormatter,
                fieldExpressionPrefix: '$.File.'
            });
        }

        /**
         * Return the system fields for an entity in a structure supposed by the Tree control.
         *
         * @param {object} fieldFormatter - Optional. One of the field formatters in FieldSelectorService.fieldFormatters
         * @param {string} entityTemplateName - Name of the entities entity template.
         *
         * @return {array} An array of nodes for use in the field selector tree control.
        */
        function getEntitySystemFields(fieldFormatter, entityTemplateName) {
            var fields = [
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Email Address', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Email Display Name', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Number', HelpText: null, Length: null, Mandatory: true, Name: 'ID', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Date', HelpText: null, Length: null, Mandatory: true, Name: 'Last Updated Date', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Last Updated By', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Date', HelpText: null, Length: null, Mandatory: true, Name: 'Created Date', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Created By', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Preview Name', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Preview Description', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Item Count', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Task Count', Visible: true, Width: 0 }];

            return convertEntityTemplateFields({
                fields: fields,
                fieldFormatter: fieldFormatter,
                fieldExpressionPrefix: '$.' + entityTemplateName + '.'
            });
        }

        /**
         * Return the system fields for an entity preview in a structure supposed by the Tree control.
         *
         * @param {object} fieldFormatter - Optional. One of the field formatters in FieldSelectorService.fieldFormatters
         * @param {string} entityTemplateName - Name of the entities entity template.
         *
         * @return {array} An array of nodes for use in the field selector tree control.
        */
        function getEntitySystemForPreviewFields(fieldFormatter, entityTemplateName) {
            var fields = [
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Email Address', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Number', HelpText: null, Length: null, Mandatory: true, Name: 'ID', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Date', HelpText: null, Length: null, Mandatory: true, Name: 'Last Updated Date', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Date', HelpText: null, Length: null, Mandatory: true, Name: 'Created Date', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Item Count', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Task Count', Visible: true, Width: 0 }];

            return convertEntityTemplateFields({
                fields: fields,
                fieldFormatter: fieldFormatter,
                fieldExpressionPrefix: '$.' + entityTemplateName + '.'
            });
        }

        /**
         * Return the system fields for an entity search in a structure supposed by the Tree control.
         *
         * @param {object} fieldFormatter - Optional. One of the field formatters in FieldSelectorService.fieldFormatters
         * @param {string} entityTemplateName - Name of the entities entity template.
         *
         * @return {array} An array of nodes for use in the field selector tree control.
        */
        function getEntitySystemForSearchFields(fieldFormatter, entityTemplateName) {
            var fields = [
                { Default: null, Description: '', FieldType: 'Number', HelpText: null, Length: null, Mandatory: true, Name: 'ID', Visible: true, Width: 0 },
                { Default: null, Description: '', FieldType: 'Text', HelpText: null, Length: null, Mandatory: true, Name: 'Email Address', Visible: true, Width: 0 }];

            return convertEntityTemplateFields({
                fields: fields,
                fieldFormatter: fieldFormatter,
                fieldExpressionPrefix: '$.' + entityTemplateName + '.'
            });
        }
    }

    /**
     * @constructor
     *
     * Format fields in the style used entity template preview expression.
     * E.g. "['Cats'][*]['Species']"
     */
    function FieldFormatterEntityPreview() {
        return {
            convertNodePathToExpression: convertNodePathToExpression,
            joinCharacter: ''
        };

        /**
         * @param {array} nodePath - A list of nodes for a path in root-to-leaf order.
         * @param {string} prefix - Optional. A prefix for the start of the expression, e.g. '$.Organisation.'
         *
         * @return {string} An expression based on the node path. E.g. "['Cats'][*]['Species']"
         */
        function convertNodePathToExpression (nodePath, prefix) {
            var expression = _.map(nodePath, function (node) {
                var fieldExpression = '[\'' + node.field.Name + '\']';
                if (node.field.FieldType === 'Relationship' && node.field.Cardinality === 'Many') {
                    fieldExpression += '[*]';
                }
                return fieldExpression;
            }).join('');

            if (!_.isNil(prefix)) {
                expression = prefix + expression;
            }

            return expression;
        }
    }

    /**
     * @constructor
     *
     * Format fields in the style used in document template merge fields.
     * Also used in entity template email display name.
     * E.g. "[[ChilliDocs:Cats.Species]]"
     */
    function FieldFormatterTemplateMerge() {
        var expressionPrefix = '[[ChilliDocs:';
        var expressionSuffix = ']]';
        var joinCharacter = '.';

        return {
            convertNodePathToExpression: convertNodePathToExpression,
            joinCharacter: joinCharacter
        };

        /**
         * @param {array} nodePath - A list of nodes for a path in root-to-leaf order.
         * @param {string} prefix - Optional. A prefix for the start of the expression, e.g. '$.Organisation.'
         *
         * @return {string} An expression based on the node path. E.g. "[[ChilliDocs:Cats.Species]]"
         */
        function convertNodePathToExpression(nodePath, prefix) {
            var expression = _.map(nodePath, 'field.Name').join(joinCharacter);

            if (!_.isNil(prefix)) {
                expression = prefix + expression;
            }

            return expressionPrefix + expression + expressionSuffix;
        }
    }

    /**
     * @constructor
     *
     * Format fields in a simple format.
     * E.g. Cats.Species
     */
    function FieldFormatterSimple() {
        var joinCharacter = '.';

        return {
            convertNodePathToExpression: convertNodePathToExpression,
            joinCharacter: joinCharacter
        };

        /**
         * @param {array} nodePath - A list of nodes for a path in root-to-leaf order.
         * @param {string} prefix - Optional. A prefix for the start of the expression, e.g. '$.Organisation.'
         *
         * @return {string} An expression based on the node path. E.g. "Cats.Species"
         */
        function convertNodePathToExpression(nodePath, prefix) {
            var expression = _.map(nodePath, 'field.Name').join(joinCharacter);

            if (!_.isNil(prefix)) {
                expression = prefix + expression;
            }

            return expression;
        }
    }
})();
